import React, { useEffect, useState } from "react";

import { useForm, Controller, SubmitHandler } from "react-hook-form";
import SurveyFooter from "../SurveyFooter";
import SurveyTitle from "../SurveyTitleV2";
import { useTranslation } from "next-i18next";
import { answerQuestions } from "@/hooks/useRequest/useSurveyRequest";
import { AnswerQuestionModel } from "@/interfaces/surveyTypes";
import { useRouter } from "next/router";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import clsx from "clsx";
import ValidateError from "@/containers/common/ValidateError";
import log2Sentry from "@/utils/log2Sentry";
import ContentContainer from "@/containers/common/ContentContainer";
import Input from "@/components/Input";

interface IFormInput {
	firstName: string;
	middleName: string;
	lastName: string;
}

interface NameProps {
	onNextClick: () => void;
	answerFieldToValueMap: Map<string, string>;
	questionFieldToIdMap: Map<string, string>;
	orcFirstName?: string;
	orcMiddleName?: string;
	orcLastName?: string;
}
function Name(props: NameProps) {
	const { t } = useTranslation("survey");
	const { locale } = useRouter();
	const [loading, setLoading] = useState(false);
	const { control, handleSubmit, setValue, formState } = useForm<IFormInput>({
		defaultValues: {
			firstName: "",
			middleName: "",
			lastName: "",
		},
	});

	const { isDirty, isValid, errors } = formState;

	const column = () => {
		return locale?.toLocaleLowerCase() === "zh-hans" ? "flex-col-reverse" : "flex-col";
	};

	useEffect(() => {
		setValue("firstName", props.answerFieldToValueMap.get("firstName") || "", {
			shouldValidate: props.answerFieldToValueMap.get("firstName") ? true : false,
		});
		setValue("middleName", props.answerFieldToValueMap.get("middleName") || "", {
			shouldValidate: props.answerFieldToValueMap.get("middleName") ? true : false,
		});
		setValue("lastName", props.answerFieldToValueMap.get("lastName") || "", {
			shouldValidate: props.answerFieldToValueMap.get("lastName") ? true : false,
		});
	}, []);

	const onSubmit: SubmitHandler<IFormInput> = async (data) => {
		try {
			const firstNameQuestionId = props.questionFieldToIdMap.get("firstName");
			const middleNameQuestionId = props.questionFieldToIdMap.get("middleName");
			const lastNameQuestionId = props.questionFieldToIdMap.get("lastName");
			const isQuestionIdMissing = !firstNameQuestionId || !middleNameQuestionId || !lastNameQuestionId;

			if (isQuestionIdMissing) {
				throw new Error("question id not exist");
			}

			const payload: AnswerQuestionModel[] = [
				{
					answer: data.firstName,
					questionId: firstNameQuestionId,
				},
				{
					answer: data.middleName,
					questionId: middleNameQuestionId,
				},
				{
					answer: data.lastName,
					questionId: lastNameQuestionId,
				},
			];

			setLoading(true);
			await answerQuestions({ payload });
			props.onNextClick();
		} catch (error) {
			log2Sentry(error, "error", error);
		} finally {
			setLoading(false);
			log2sensors(SensorsEventIds.AO_NAME_NEXTPAGE);
		}
	};

	useEffect(() => {
		log2sensors(SensorsEventIds.AO_NAME_VIEW);
	}, []);

	return (
		<ContentContainer
			className="px-4.5"
			animation
			footer={
				<SurveyFooter
					disabled={!isValid}
					loading={loading}
					onNextBtnClick={handleSubmit(onSubmit)}
					onPrivacyClick={() => log2sensors(SensorsEventIds.AO_NAME_PRAVICY)}
					onSecureClick={() => log2sensors(SensorsEventIds.AO_NAME_SECURITY)}
				/>
			}
		>
			<SurveyTitle title={t("what-is-your-legal-name")} subtitle={t("must-exactly-match-the-document")} />
			<div className="h-12" />

			<div className={clsx("flex", column())}>
				<div className="mb-2.5">
					<Controller
						name="firstName"
						control={control}
						rules={{
							required: t("this-field-must-not-be-empty"),
							maxLength: {
								value: 128,
								message: t("maximum-word-limit-exceeded"),
							},
						}}
						defaultValue=""
						render={({ field }) => (
							<Input
								{...field}
								placeholder={t("first-name")}
								onClick={() => log2sensors(SensorsEventIds.AO_NAME_FIRSTNAME)}
							/>
						)}
					/>

					<ValidateError error={errors.firstName} />
				</div>

				<div className="mb-2.5">
					<Controller
						name="middleName"
						control={control}
						rules={{
							maxLength: {
								value: 128,
								message: t("maximum-word-limit-exceeded"),
							},
						}}
						defaultValue=""
						render={({ field }) => (
							<Input
								{...field}
								placeholder={t("middle-name")}
								onClick={() => log2sensors(SensorsEventIds.AO_NAME_MIDDLENAME)}
							/>
						)}
					/>
				</div>

				<div className="mb-2.5">
					<Controller
						name="lastName"
						rules={{
							required: t("this-field-must-not-be-empty"),
							maxLength: {
								value: 128,
								message: t("maximum-word-limit-exceeded"),
							},
						}}
						control={control}
						defaultValue=""
						render={({ field }) => (
							<Input
								{...field}
								placeholder={t("last-name")}
								onClick={() => log2sensors(SensorsEventIds.AO_NAME_LASTNAME)}
							/>
						)}
					/>

					<ValidateError error={errors.lastName} />
				</div>
			</div>
		</ContentContainer>
	);
}

export default Name;
